<template>
  <div class="debug-page">
    <header class="app-header">
      <div class="brand">
        <div class="brand-mark" aria-hidden="true">G</div>
        <div class="brand-text">
          <div class="brand-title-row">
            <h1>Gooval Open API</h1>
            <time
              v-if="buildTimeUtc"
              class="build-time-utc"
              :datetime="buildTimeUtc"
              title="构建 UTC 时间"
            >
              {{ buildTimeUtc }}
            </time>
          </div>
          <p>开放平台接口调试</p>
        </div>
      </div>
      <div class="header-env">
        <el-tag size="small" effect="dark" type="info">TEST</el-tag>
        <code class="env-url">/open/v1</code>
      </div>
      <el-tag type="warning" effect="light" class="header-warn">仅本地调试</el-tag>
    </header>

    <el-card shadow="hover" class="toolbar-card">
      <div class="toolbar-row">
        <div class="toolbar-block cred-block">
          <span class="toolbar-label">凭证</span>
          <el-tag
            :type="credentialsConfigured ? 'success' : 'info'"
            effect="plain"
            round
          >
            {{ credentialSummary }}
          </el-tag>
          <el-button type="primary" plain size="small" @click="openCredentialDialog">
            {{ credentialsConfigured ? '修改' : '配置' }}
          </el-button>
        </div>
      </div>
      <p class="toolbar-hint">
        金额字段为整数最小货币单位（如 USD 10.00 → 1000）；写操作自动生成 Idempotency-Key；
        每次请求附带 X-Request-Id 便于对照服务端日志。HMAC 仅供联调，生产环境请在服务端签名。
      </p>
    </el-card>

    <div class="workspace-shell">
      <aside class="nav-panel">
        <div class="nav-panel-head">
          <div class="nav-panel-head-main">
            <span class="nav-panel-title">接口目录</span>
            <el-tag size="small" type="info" effect="plain" round>{{ totalEndpointCount }}</el-tag>
          </div>
          <div class="nav-panel-actions">
            <el-button type="primary" link size="small" @click="expandAllNavGroups">全部展开</el-button>
            <el-button type="info" link size="small" @click="collapseAllNavGroups">全部收起</el-button>
          </div>
        </div>
        <div class="nav-search">
          <el-input
            v-model="endpointFilter"
            placeholder="搜索名称或路径"
            clearable
            size="default"
          />
        </div>
        <div class="nav-scroll">
          <div v-if="!filteredNavGroups.length" class="nav-empty">无匹配接口</div>
          <section
            v-for="group in filteredNavGroups"
            :key="group.id"
            class="nav-group"
          >
            <button
              type="button"
              class="nav-group-toggle"
              :aria-expanded="!collapsedGroups[group.id]"
              @click="toggleNavGroup(group.id)"
            >
              <span class="nav-group-label">{{ group.label }}</span>
              <span class="nav-group-meta">
                <el-tag size="small" effect="plain" round>{{ group.endpoints.length }}</el-tag>
                <span class="chevron" :class="{ 'chevron--open': !collapsedGroups[group.id] }">›</span>
              </span>
            </button>
            <div
              v-show="!collapsedGroups[group.id] || endpointFilter.trim()"
              class="nav-group-list"
            >
              <button
                v-for="ep in group.endpoints"
                :key="ep.id"
                type="button"
                class="nav-endpoint"
                :class="{ 'nav-endpoint--active': selectedId === ep.id }"
                @click="selectEndpoint(ep.id)"
              >
                <span
                  class="method-pill"
                  :class="ep.method === 'GET' ? 'method-pill--get' : 'method-pill--post'"
                >
                  {{ ep.method }}
                </span>
                <span class="nav-endpoint-text">
                  <span class="nav-endpoint-summary">{{ ep.summary || ep.label }}</span>
                  <span class="nav-endpoint-path">{{ endpointPathLabel(ep) }}</span>
                </span>
              </button>
            </div>
          </section>
        </div>
      </aside>

      <main v-if="current" class="workspace">
        <div class="request-strip">
          <span
            class="method-pill method-pill--lg"
            :class="current.method === 'GET' ? 'method-pill--get' : 'method-pill--post'"
          >
            {{ current.method }}
          </span>
          <code class="url-preview">{{ requestUrlPreview }}</code>
          <el-button
            type="primary"
            size="large"
            class="send-btn"
            :loading="loading"
            @click="sendRequest"
          >
            发送请求
          </el-button>
        </div>

        <el-alert
          v-if="current.hint"
          type="info"
          :closable="false"
          show-icon
          class="endpoint-hint"
          :title="current.hint"
        />

        <el-card v-if="editablePathParamKeys.length" shadow="hover" class="section-card">
          <template #header>
            <span class="section-title">路径参数</span>
          </template>
          <el-form label-position="top" :inline="true">
            <el-form-item v-for="key in editablePathParamKeys" :key="key" :label="key">
              <el-input
                :model-value="pathParams[key]"
                :placeholder="`{${key}}`"
                clearable
                style="width: 220px"
                @update:model-value="(v) => setPathParam(key, v)"
              />
            </el-form-item>
          </el-form>
        </el-card>

        <el-card v-if="queryFields.length" shadow="hover" class="section-card">
          <template #header>
            <span class="section-title">Query</span>
          </template>
          <el-form label-position="top" :inline="true">
            <el-form-item v-for="q in queryFields" :key="q.key" :label="q.key">
              <el-input
                :model-value="q.value"
                :placeholder="q.placeholder || `{${q.key}}`"
                style="width: 220px"
                clearable
                @update:model-value="(v) => setQueryParam(q.key, v)"
              />
            </el-form-item>
          </el-form>
        </el-card>

        <el-card
          v-if="current.method !== 'GET' && current.method !== 'HEAD'"
          shadow="hover"
          class="section-card"
        >
          <template #header>
            <div class="section-card-head">
              <div class="section-card-head-main">
                <span class="section-title">Request Body</span>
                <el-tag size="small" type="info" effect="plain">JSON</el-tag>
              </div>
              <el-button type="primary" link size="small" @click="formatRequestBody">
                格式化 JSON
              </el-button>
            </div>
          </template>
          <el-input
            v-model="bodyText"
            type="textarea"
            :rows="14"
            spellcheck="false"
            class="mono-input"
          />
          <p v-if="bodyError" class="error-text">{{ bodyError }}</p>
        </el-card>

        <el-card v-if="current.id === 'upload-url'" shadow="hover" class="section-card">
          <template #header>
            <span class="section-title">附件上传（第二步）</span>
          </template>
          <p class="hint">先调用 upload-url，再对返回的 uploadUrl 执行 PUT。</p>
          <el-upload :auto-upload="false" :limit="1" :on-change="onFileChange">
            <el-button type="default">选择文件</el-button>
          </el-upload>
          <el-button
            type="success"
            class="upload-btn"
            :disabled="!lastUploadUrl || !uploadFile"
            :loading="uploadLoading"
            @click="putUpload"
          >
            PUT 到 uploadUrl
          </el-button>
          <el-text v-if="uploadResult" type="info" class="upload-result">{{ uploadResult }}</el-text>
        </el-card>

        <el-card shadow="hover" class="section-card response-card">
          <template #header>
            <div class="response-head">
              <span class="section-title">请求 / 响应</span>
              <template v-if="lastResponse">
                <el-tag :type="statusTagType" effect="dark" round>
                  {{ lastResponse.status }} {{ lastResponse.statusText }}
                </el-tag>
                <el-text type="info" class="duration-text">{{ lastResponse.durationMs }} ms</el-text>
              </template>
            </div>
          </template>
          <el-empty
            v-if="!lastRequest && !lastResponse && !requestError"
            description="选择接口并发送请求"
            :image-size="72"
          />
          <el-tabs v-if="lastRequest || lastResponse" v-model="responseTab" class="response-tabs">
            <el-tab-pane v-if="lastRequest" label="Request Headers" name="requestHeaders">
              <p class="request-line">
                <el-tag size="small" :type="lastRequest.method === 'GET' ? 'success' : 'primary'">
                  {{ lastRequest.method }}
                </el-tag>
                <code>{{ lastRequest.url }}</code>
              </p>
              <pre class="code-block">{{ lastRequest.headersText }}</pre>
            </el-tab-pane>
            <el-tab-pane v-if="lastResponse" label="Response Body" name="body">
              <pre class="code-block body-block">{{ lastResponse.bodyText }}</pre>
            </el-tab-pane>
            <el-tab-pane v-if="lastResponse" label="Response Headers" name="headers">
              <pre class="code-block">{{ lastResponse.headersText }}</pre>
            </el-tab-pane>
          </el-tabs>
          <el-alert
            v-if="requestError"
            type="error"
            :title="requestError"
            show-icon
            :closable="false"
            class="err-alert"
          />
        </el-card>
      </main>
    </div>

    <el-dialog
      v-model="credentialDialogVisible"
      title="Gooval Open API 凭证"
      width="480px"
      :close-on-click-modal="false"
      :show-close="!credentialDialogRequired"
      @closed="onCredentialDialogClosed"
    >
      <p class="dialog-hint">
        填写 Admin 开通凭证后，将并行请求 product-categories / countries / cancel-reasons；
        <strong>任一接口返回成功</strong> 后才会写入本机 localStorage。
      </p>
      <el-form
        ref="credentialFormRef"
        :model="credentialForm"
        :rules="credentialRules"
        label-position="top"
      >
        <el-form-item label="Store Number" prop="storeNumber">
          <el-input v-model="credentialForm.storeNumber" placeholder="X-Gwofy-Store-Number" clearable />
        </el-form-item>
        <el-form-item label="API Key ID" prop="apiKeyId">
          <el-input v-model="credentialForm.apiKeyId" placeholder="gwofy_…" clearable />
        </el-form-item>
        <el-form-item label="API Secret" prop="apiSecret">
          <el-input
            v-model="credentialForm.apiSecret"
            type="password"
            placeholder="开通时返回的 Secret"
            show-password
            autocomplete="off"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button v-if="!credentialDialogRequired" @click="credentialDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="credentialVerifying" @click="confirmCredentials">
          验证并保存
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import {
  endpointGroups,
  findEndpointById,
  findEndpointByHashId,
  DEFAULT_ENDPOINT_HASH_ID,
  resolvePath,
  resolvePathPreview,
  buildQueryString
} from '@/config/openEndpoints'
import { DEFAULT_API_HOST, BUILD_TIME_UTC } from '@/config/env'
import {
  hmacSha256Hex,
  buildSignaturePayload,
  unixTimestampSeconds,
  newIdempotencyKey
} from '@/utils/goovalAuth'
import http from '@/utils/http'
import { buildOpenApiUrl } from '@/utils/apiUrl'
import {
  loadCredentials,
  saveCredentials,
  needsCredentialDialog,
  credentialsReadyForRequest
} from '@/utils/openApiCredentials'
import { verifyOpenApiCredentials } from '@/utils/verifyOpenApiCredentials'

function cloneEndpointState (ep) {
  return {
    pathParams: { ...ep.pathParams },
    queryParams: (ep.queryParams || []).map((q) => ({ ...q })),
    body: ep.body != null ? JSON.parse(JSON.stringify(ep.body)) : null
  }
}

function headersToText (headers) {
  return Object.keys(headers || {})
    .sort((a, b) => a.localeCompare(b))
    .map((k) => `${k}: ${headers[k]}`)
    .join('\n')
}

function prettyJsonText (text) {
  const trimmed = (text ?? '').trim()
  if (!trimmed) {
    return { ok: true, text: '' }
  }
  try {
    return { ok: true, text: JSON.stringify(JSON.parse(trimmed), null, 2) }
  } catch {
    return { ok: false, error: 'JSON 格式无效' }
  }
}

function formatBodyText (data, responseType) {
  if (responseType === 'blob' && data instanceof Blob) {
    if (data.type.includes('json')) {
      return data.text().then((text) => {
        try {
          return JSON.stringify(JSON.parse(text), null, 2)
        } catch {
          return text
        }
      })
    }
    return Promise.resolve(`[binary ${data.size} bytes, type: ${data.type || 'unknown'}]`)
  }
  if (typeof data === 'string') {
    try {
      return Promise.resolve(JSON.stringify(JSON.parse(data), null, 2))
    } catch {
      return Promise.resolve(data)
    }
  }
  if (data !== null && typeof data === 'object') {
    return Promise.resolve(JSON.stringify(data, null, 2))
  }
  return Promise.resolve(String(data ?? ''))
}

export default {
  name: 'OpenApiDebugView',
  props: {
    hashId: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      endpointGroups,
      endpointFilter: '',
      collapsedGroups: {},
      selectedId: '',
      credentials: loadCredentials(),
      credentialDialogVisible: false,
      credentialDialogRequired: false,
      credentialVerifying: false,
      credentialForm: {
        storeNumber: '',
        apiKeyId: '',
        apiSecret: ''
      },
      credentialRules: {
        storeNumber: [{ required: true, message: '请填写 Store Number', trigger: 'blur' }],
        apiKeyId: [{ required: true, message: '请填写 API Key ID', trigger: 'blur' }],
        apiSecret: [{ required: true, message: '请填写 API Secret', trigger: 'blur' }]
      },
      pathParams: {},
      queryFields: [],
      bodyText: '{}',
      bodyError: '',
      loading: false,
      requestError: '',
      lastRequest: null,
      lastResponse: null,
      lastUploadUrl: '',
      uploadFile: null,
      uploadLoading: false,
      uploadResult: '',
      responseTab: 'body'
    }
  },
  computed: {
    credentialsConfigured () {
      return credentialsReadyForRequest(this.credentials)
    },
    totalEndpointCount () {
      return this.endpointGroups.reduce((n, g) => n + g.endpoints.length, 0)
    },
    filteredNavGroups () {
      const q = this.endpointFilter.trim().toLowerCase()
      if (!q) {
        return this.endpointGroups
      }
      return this.endpointGroups
        .map((group) => ({
          ...group,
          endpoints: group.endpoints.filter((ep) => this.endpointMatchesFilter(ep, q))
        }))
        .filter((group) => group.endpoints.length > 0)
    },
    current () {
      return findEndpointById(this.selectedId)
    },
    pathParamKeys () {
      if (!this.current) return []
      return Object.keys(this.pathParams)
    },
    editablePathParamKeys () {
      const ep = this.current
      if (!ep) return []
      const locked = new Set(ep.readOnlyPathParams || [])
      return this.pathParamKeys.filter((key) => !locked.has(key))
    },
    apiHost () {
      return DEFAULT_API_HOST
    },
    buildTimeUtc () {
      return BUILD_TIME_UTC
    },
    /** 顶部 URL 条：仅展示接口模板，不随路径/Query 表单联动 */
    requestUrlPreview () {
      if (!this.current) return ''
      try {
        const path = resolvePathPreview(this.current.path, this.current.pathParams || {})
        const qs = buildQueryString(this.current.queryParams || [])
        return `/open/v1${path}${qs}`
      } catch (e) {
        return e.message
      }
    },
    queryFieldsSnapshot () {
      return this.queryFields.map((q) => ({
        key: q.key,
        value: q.value,
        placeholder: q.placeholder
      }))
    },
    statusTagType () {
      if (!this.lastResponse) return 'info'
      const s = this.lastResponse.status
      if (s >= 200 && s < 300) return 'success'
      if (s >= 400) return 'danger'
      return 'warning'
    },
    credentialSummary () {
      if (!credentialsReadyForRequest(this.credentials)) {
        return '未配置'
      }
      const sn = this.credentials.storeNumber
      const kid = this.credentials.apiKeyId
      const mask = kid.length > 8 ? `${kid.slice(0, 6)}…${kid.slice(-4)}` : kid
      return `Store ${sn} · Key ${mask}`
    }
  },
  watch: {
    bodyText () {
      this.bodyError = ''
    },
    hashId: {
      immediate: true,
      handler (hashId) {
        this.syncEndpointFromRoute(hashId)
      }
    }
  },
  mounted () {
    this.ensureCredentialsDialog()
  },
  created () {
    this.initNavGroups()
  },
  methods: {
    syncEndpointFromRoute (hashId) {
      const ep =
        findEndpointByHashId(hashId) ||
        findEndpointByHashId(DEFAULT_ENDPOINT_HASH_ID)
      if (!ep) return
      if (ep.id !== this.selectedId) {
        this.loadEndpointState(ep.id)
      }
      const routeHash = String(hashId || '').trim()
      if (routeHash !== ep.hashId) {
        this.$router.replace({ name: 'open-api-debug', params: { hashId: ep.hashId } })
      }
    },
    initNavGroups () {
      const collapsed = {}
      for (const g of this.endpointGroups) {
        collapsed[g.id] = false
      }
      this.collapsedGroups = collapsed
    },
    toggleNavGroup (groupId) {
      this.collapsedGroups = {
        ...this.collapsedGroups,
        [groupId]: !this.collapsedGroups[groupId]
      }
    },
    expandAllNavGroups () {
      const next = {}
      for (const g of this.endpointGroups) {
        next[g.id] = false
      }
      this.collapsedGroups = next
    },
    collapseAllNavGroups () {
      const next = {}
      for (const g of this.endpointGroups) {
        next[g.id] = true
      }
      this.collapsedGroups = next
    },
    /** 侧栏路径：接口模板，不随表单联动 */
    endpointPathLabel (ep) {
      if (!ep.path) return ''
      return resolvePathPreview(ep.path, ep.pathParams || {})
    },
    endpointMatchesFilter (ep, q) {
      const parts = [
        ep.summary,
        ep.label,
        ep.path,
        ep.method,
        ep.hashId,
        this.endpointPathLabel(ep)
      ]
      return parts.some((p) => String(p || '').toLowerCase().includes(q))
    },
    ensureCredentialsDialog () {
      if (needsCredentialDialog(this.credentials) || !this.credentials.storeNumber) {
        this.openCredentialDialog(true)
      }
    },
    openCredentialDialog (required = false) {
      this.credentialDialogRequired = required
      this.credentialForm = {
        storeNumber: this.credentials.storeNumber,
        apiKeyId: this.credentials.apiKeyId,
        apiSecret: this.credentials.apiSecret
      }
      this.credentialDialogVisible = true
      this.$nextTick(() => this.$refs.credentialFormRef?.clearValidate())
    },
    onCredentialDialogClosed () {
      if (this.credentialDialogRequired && !credentialsReadyForRequest(this.credentials)) {
        this.$nextTick(() => this.openCredentialDialog(true))
      }
    },
    async confirmCredentials () {
      const form = this.$refs.credentialFormRef
      if (!form) return
      try {
        await form.validate()
      } catch {
        return
      }
      this.credentialVerifying = true
      try {
        const result = await verifyOpenApiCredentials(
          {
            storeNumber: this.credentialForm.storeNumber,
            apiSecret: this.credentialForm.apiSecret
          },
          this.apiHost
        )
        if (!result.valid) {
          this.$message.error(result.message || '凭证校验失败')
          return
        }
        this.credentials = saveCredentials(this.credentialForm)
        this.credentialDialogRequired = false
        this.credentialDialogVisible = false
        this.$message.success(`凭证校验通过（${result.path} ${result.status}），已保存到本机`)
      } finally {
        this.credentialVerifying = false
      }
    },
    setPathParam (key, value) {
      const locked = new Set(this.current?.readOnlyPathParams || [])
      if (locked.has(key)) return
      this.pathParams = {
        ...this.pathParams,
        [key]: value
      }
    },
    setQueryParam (key, value) {
      this.queryFields = this.queryFields.map((q) =>
        q.key === key ? { ...q, value } : { ...q }
      )
    },
    selectEndpoint (id) {
      const ep = findEndpointById(id)
      if (!ep) return
      this.loadEndpointState(id)
      if (ep.hashId && this.$route.params.hashId !== ep.hashId) {
        this.$router.push({ name: 'open-api-debug', params: { hashId: ep.hashId } })
      }
    },
    loadEndpointState (id) {
      const ep = findEndpointById(id)
      if (!ep) return
      this.selectedId = id
      const st = cloneEndpointState(ep)
      this.pathParams = { ...st.pathParams }
      for (const key of ep.readOnlyPathParams || []) {
        if (st.pathParams[key] !== undefined) {
          this.pathParams[key] = st.pathParams[key]
        }
      }
      this.queryFields = st.queryParams.map((q) => ({ ...q }))
      this.bodyText = st.body != null ? JSON.stringify(st.body, null, 2) : ''
      this.bodyError = ''
      this.requestError = ''
      this.lastRequest = null
      this.lastResponse = null
      this.responseTab = 'requestHeaders'
    },
    formatRequestBody () {
      const result = prettyJsonText(this.bodyText)
      if (!result.ok) {
        this.bodyError = result.error
        return
      }
      this.bodyText = result.text
      this.bodyError = ''
    },
    parseBody () {
      if (this.current.method === 'GET' || this.current.method === 'HEAD') {
        return { raw: '', parsed: null }
      }
      const trimmed = this.bodyText.trim()
      if (!trimmed) return { raw: '', parsed: null }
      try {
        const parsed = JSON.parse(trimmed)
        return { raw: JSON.stringify(parsed), parsed }
      } catch (e) {
        this.bodyError = 'JSON 格式无效'
        throw e
      }
    },
    async sendRequest () {
      this.requestError = ''
      this.lastRequest = null
      this.lastResponse = null
      this.responseTab = 'requestHeaders'
      if (!credentialsReadyForRequest(this.credentials)) {
        this.openCredentialDialog(true)
        return
      }

      let rawBody = ''
      try {
        const b = this.parseBody()
        rawBody = b.raw
      } catch {
        return
      }

      let path
      try {
        path = resolvePath(this.current.path, this.pathParams)
      } catch (e) {
        this.requestError = e.message
        return
      }

      const qs = buildQueryString(this.queryFieldsSnapshot)
      const url = buildOpenApiUrl(path, qs, this.apiHost)

      const timestamp = unixTimestampSeconds()
      const storeNumber = this.credentials.storeNumber
      const apiSecret = this.credentials.apiSecret
      const payload = buildSignaturePayload(storeNumber, timestamp, rawBody)
      const signature = await hmacSha256Hex(apiSecret, payload)

      const headers = {
        'X-Gwofy-Store-Number': storeNumber,
        'X-Gwofy-Timestamp': timestamp,
        'X-Gwofy-Signature': signature,
        'X-Request-Id': newIdempotencyKey()
      }
      if (this.current.idempotent) {
        headers['Idempotency-Key'] = newIdempotencyKey()
      }
      if (rawBody) {
        headers['Content-Type'] = 'application/json'
      }

      this.lastRequest = {
        method: this.current.method,
        url: buildOpenApiUrl(path, qs, ''),
        headersText: headersToText(headers)
      }

      const responseType = this.current.responseType === 'blob' ? 'blob' : 'json'

      this.loading = true
      const start = performance.now()
      try {
        const res = await http.request({
          url,
          method: this.current.method,
          headers,
          data: rawBody || undefined,
          responseType,
          transformRequest: rawBody
            ? [(data, hdrs) => {
              if (hdrs['Content-Type'] === 'application/json' && typeof data === 'string') {
                return data
              }
              return data
            }]
            : undefined
        })

        const durationMs = Math.round(performance.now() - start)
        const headersText = headersToText(res.headers || {})

        const bodyText = await formatBodyText(res.data, responseType)

        this.lastResponse = {
          status: res.status,
          statusText: res.statusText,
          durationMs,
          headersText,
          bodyText
        }
        this.responseTab = 'body'

        if (this.current.id === 'upload-url' && res.status >= 200 && res.status < 300) {
          try {
            const data = typeof res.data === 'object' ? res.data : JSON.parse(bodyText)
            this.lastUploadUrl = data.uploadUrl || data.upload_url || ''
          } catch (_) { /* ignore */ }
        }
      } catch (e) {
        this.requestError = e.message || String(e)
      } finally {
        this.loading = false
      }
    },
    onFileChange (uploadFile) {
      this.uploadFile = uploadFile?.raw || null
      this.uploadResult = ''
    },
    async putUpload () {
      if (!this.lastUploadUrl || !this.uploadFile) return
      this.uploadLoading = true
      this.uploadResult = ''
      try {
        const res = await http.put(this.lastUploadUrl, this.uploadFile, {
          headers: {
            'Content-Type': this.uploadFile.type || 'application/octet-stream'
          },
          validateStatus: () => true
        })
        this.uploadResult = `PUT ${res.status} ${res.statusText}`
      } catch (e) {
        this.uploadResult = `上传失败: ${e.message}`
      } finally {
        this.uploadLoading = false
      }
    }
  }
}
</script>

<style scoped>
.debug-page {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px 24px 40px;
}

.app-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px 24px;
  padding: 20px 24px;
  margin-bottom: 20px;
  border-radius: 14px;
  background: linear-gradient(135deg, var(--gooval-header) 0%, #1e293b 55%, #134e4a 100%);
  color: #f8fafc;
  box-shadow: 0 12px 40px rgba(15, 23, 42, 0.18);
}

.brand {
  display: flex;
  align-items: center;
  gap: 14px;
  flex: 1;
  min-width: 200px;
}

.brand-mark {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 20px;
  background: linear-gradient(145deg, #14b8a6, #0d9488);
  color: #fff;
  box-shadow: 0 4px 14px rgba(13, 148, 136, 0.45);
}

.brand-title-row {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px 12px;
}

.brand-text h1 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  letter-spacing: -0.02em;
}

.build-time-utc {
  font-size: 0.6875rem;
  font-weight: 500;
  color: #94a3b8;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
}

.brand-text p {
  margin: 4px 0 0;
  font-size: 0.8125rem;
  color: #94a3b8;
}

.header-env {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.env-url {
  font-size: 12px;
  padding: 6px 12px;
  border-radius: 8px;
  background: rgba(15, 23, 42, 0.45);
  border: 1px solid rgba(148, 163, 184, 0.25);
  color: #e2e8f0;
  font-family: ui-monospace, Menlo, Monaco, Consolas, monospace;
}

.header-warn {
  margin-left: auto;
}

.toolbar-card {
  margin-bottom: 20px;
  border-radius: 12px;
  border: 1px solid var(--el-border-color-lighter);
}

.toolbar-card :deep(.el-card__body) {
  padding: 16px 20px;
}

.toolbar-row {
  display: flex;
  flex-wrap: wrap;
  gap: 20px 32px;
  align-items: flex-end;
}

.toolbar-block {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.toolbar-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--el-text-color-secondary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  width: 100%;
}

.toolbar-hint {
  margin: 12px 0 0;
  font-size: 12px;
  color: var(--el-text-color-secondary);
  line-height: 1.5;
}

.endpoint-hint {
  margin-bottom: 16px;
}

.workspace-shell {
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 16px;
  align-items: stretch;
}

@media (max-width: 960px) {
  .workspace-shell {
    grid-template-columns: 1fr;
  }

  .nav-panel {
    max-height: min(70vh, 520px);
  }
}

.nav-panel {
  position: sticky;
  top: 16px;
  align-self: start;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 32px);
  background: #fff;
  border-radius: 12px;
  border: 1px solid var(--el-border-color-lighter);
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
}

.nav-panel-head {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 14px;
  border-bottom: 1px solid var(--el-border-color-lighter);
  background: var(--el-fill-color-blank);
}

.nav-panel-head-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.nav-panel-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.nav-panel-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--el-text-color-primary);
}

.nav-search {
  flex-shrink: 0;
  padding: 10px 12px;
  border-bottom: 1px solid var(--el-border-color-extra-light);
  background: #fff;
}

.nav-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  padding: 8px 0 12px;
  -webkit-overflow-scrolling: touch;
}

.nav-empty {
  padding: 24px 16px;
  text-align: center;
  font-size: 13px;
  color: var(--el-text-color-secondary);
}

.nav-group {
  margin-bottom: 4px;
}

.nav-group-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 14px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: var(--el-text-color-regular);
  transition: background 0.15s;
}

.nav-group-toggle:hover {
  background: var(--el-fill-color-light);
}

.nav-group-label {
  text-align: left;
}

.nav-group-meta {
  display: flex;
  align-items: center;
  gap: 6px;
}

.chevron {
  display: inline-block;
  font-size: 16px;
  line-height: 1;
  color: var(--el-text-color-secondary);
  transform: rotate(0deg);
  transition: transform 0.2s;
}

.chevron--open {
  transform: rotate(90deg);
}

.nav-group-list {
  padding: 0 8px 6px;
}

.nav-endpoint {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  margin-bottom: 4px;
  padding: 10px 10px 10px 8px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s, border-color 0.15s, box-shadow 0.15s;
}

.nav-endpoint:hover {
  background: var(--el-fill-color-light);
}

.nav-endpoint--active {
  background: rgba(13, 148, 136, 0.08);
  border-color: rgba(13, 148, 136, 0.35);
  box-shadow: 0 1px 2px rgba(13, 148, 136, 0.08);
}

.nav-endpoint-text {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  flex: 1;
}

.nav-endpoint-summary {
  font-size: 13px;
  font-weight: 500;
  color: var(--el-text-color-primary);
  line-height: 1.3;
}

.nav-endpoint-path {
  font-size: 11px;
  line-height: 1.35;
  color: var(--el-text-color-secondary);
  font-family: ui-monospace, Menlo, Monaco, Consolas, monospace;
  word-break: break-all;
}

.method-pill {
  display: inline-block;
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.03em;
  padding: 2px 6px;
  border-radius: 4px;
  margin-right: 8px;
  vertical-align: middle;
}

.method-pill--lg {
  font-size: 11px;
  padding: 6px 10px;
  border-radius: 6px;
}

.method-pill--get {
  background: #dcfce7;
  color: #166534;
}

.method-pill--post {
  background: #dbeafe;
  color: #1d4ed8;
}

.workspace {
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
}

.request-strip {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  background: var(--gooval-header);
  border: 1px solid #334155;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
}

.request-strip .method-pill--get {
  background: #22c55e;
  color: #052e16;
}

.request-strip .method-pill--post {
  background: #3b82f6;
  color: #fff;
}

.url-preview {
  flex: 1;
  font-size: 12px;
  line-height: 1.5;
  word-break: break-all;
  min-width: 160px;
  color: #cbd5e1;
  font-family: ui-monospace, Menlo, Monaco, Consolas, monospace;
}

.send-btn {
  --el-button-bg-color: var(--gooval-primary);
  --el-button-border-color: var(--gooval-primary);
  --el-button-hover-bg-color: var(--gooval-primary-dark);
  --el-button-hover-border-color: var(--gooval-primary-dark);
}

.section-card {
  border-radius: 12px;
  border: 1px solid var(--el-border-color-lighter);
}

.section-card :deep(.el-card__header) {
  padding: 12px 18px;
  border-bottom: 1px solid var(--el-border-color-extra-light);
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  margin-right: 8px;
}

.section-card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.section-card-head-main {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.section-card-head-main .section-title {
  margin-right: 0;
}

.mono-input :deep(textarea) {
  font-family: ui-monospace, Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
  line-height: 1.55;
  background: #f8fafc;
}

.hint {
  margin: 0 0 12px;
  font-size: 13px;
  color: var(--el-text-color-secondary);
}

.upload-btn {
  margin-top: 12px;
}

.upload-result {
  display: block;
  margin-top: 8px;
}

.response-card :deep(.el-card__header) {
  padding: 10px 18px;
}

.response-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.duration-text {
  font-size: 13px;
}

.response-tabs :deep(.el-tabs__header) {
  margin-bottom: 12px;
}

.request-line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin: 0 0 10px;
  font-size: 12px;
}

.request-line code {
  font-family: ui-monospace, Menlo, Monaco, Consolas, monospace;
  word-break: break-all;
  color: var(--el-text-color-regular);
}

.code-block {
  margin: 0;
  padding: 14px 16px;
  background: #0f172a;
  color: #e2e8f0;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.55;
  overflow: auto;
  max-height: 300px;
  font-family: ui-monospace, Menlo, Monaco, Consolas, monospace;
}

.body-block {
  max-height: min(480px, 50vh);
}

.error-text {
  color: var(--el-color-danger);
  font-size: 13px;
  margin: 8px 0 0;
}

.err-alert {
  margin-top: 12px;
}

.dialog-hint {
  margin: 0 0 16px;
  font-size: 13px;
  color: var(--el-text-color-secondary);
  line-height: 1.6;
}
</style>
